/**
 * The contextual rail: the column beside `<main>` a section uses to move
 * between its parts, shared so every section's rail is the same object.
 *
 * Desk had one and the credentials page grew a second, each with its own copy
 * of the frame: the tint, the hairline edge, the grab strip, the chevron that
 * folds it, the band headings, the row washes. Two copies of a rail is how
 * one of them gets a primary-tinted selection and the other a neutral one, so
 * the chrome lives here and a section supplies only what it lists.
 *
 * **Mounted by `MainLayout`, as a sibling of `<main>`.** That is what makes it
 * run the full height of the surface. Inside the page it stopped wherever the
 * page's content stopped, which is the failure both rails had first.
 *
 * **Two widths, one rail.** Open, it is rows under band headings. Folded, it is
 * the same rows as icons, each wearing its count, with the name in a tooltip,
 * so someone working with it folded still sees the numbers move. The fold and
 * the width are preferences about the workspace rather than facts about the
 * page in view, so both are kept in the browser under the section's own keys.
 * Changing a key forgets every reader's setting, so a section keeps its keys.
 *
 * Rows use a neutral wash for hover and a stronger one for the selection, not
 * a primary tint: the rail already sits on a tinted panel, and a saturated
 * highlight on top reads as chrome stacked on chrome. Names wrap rather than
 * truncate, because the word an ellipsis hides is often the one that tells two
 * rows apart.
 */
import {
  useEffect,
  useState,
  type ElementType,
  type MouseEvent,
  type ReactNode,
} from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, PanelLeft } from "lucide-react";

import { MICRO_LABEL } from "@/components/common/typography";
import { Hint } from "@/components/ui/hint";
import { ResizeHandle } from "@/components/ui/resizable-pane";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useResizableWidth } from "@/hooks/useResizableWidth";
import { cn } from "@/lib/utils";

export interface ContextRailProps {
  /** The header's kicker, e.g. "Desk". */
  title: string;
  /** Accessible name of the rail's navigation, the same at both widths. */
  label: string;
  /** What the chevron folds, finishing "Collapse …" and "Expand …". */
  foldLabel: string;
  /** Where the width is kept. */
  widthKey: string;
  /** Where the fold is kept: "1" folded, "0" open. */
  foldKey: string;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  /** Still arriving: the frame is real and the body is a skeleton. */
  busy?: boolean;
  /** The open rail's body: rows and bands. */
  children: ReactNode;
  /** The folded rail's body: icons. */
  folded: ReactNode;
  /**
   * A line pinned under the open rail's rows, for what the reader needs to
   * read the rows right, such as the scope their counts are taken in. The
   * folded strip has no room for a sentence, so it is left out there; the
   * scope bar above the page says the same thing at every width.
   */
  footer?: ReactNode;
}

export function ContextRail({
  title,
  label,
  foldLabel,
  widthKey,
  foldKey,
  defaultWidth,
  minWidth,
  maxWidth,
  busy = false,
  children,
  folded,
  footer,
}: ContextRailProps) {
  const rail = useResizableWidth(widthKey, defaultWidth, minWidth, maxWidth);
  const [isFolded, setFolded] = useRailFold(foldKey);

  // Below `md` neither width of the rail is shown, so a phone reaches the
  // section's parts through this bar instead. Mounted only at that width, so
  // the rows exist once in the page at any width.
  const phone = usePhoneWidth() ? (
    <PhoneRail title={title} label={label} busy={busy} footer={footer}>
      {children}
    </PhoneRail>
  ) : null;

  if (isFolded) {
    return (
      <>
        {phone}
        <aside className="border-border/60 bg-sidebar/30 hidden w-12 shrink-0 flex-col items-center border-r py-3 md:flex">
          <button
            type="button"
            onClick={() => setFolded(false)}
            aria-label={`Expand ${foldLabel}`}
            className="text-muted-foreground hover:text-foreground rounded p-1.5"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          {/* Scrolls on its own: a long estate folds to a long strip. The top
            padding keeps the first icon's count badge inside the scroller,
            which would otherwise clip it. */}
          <nav
            aria-label={label}
            aria-busy={busy || undefined}
            className="mt-2 min-h-0 w-full flex-1 overflow-y-auto px-1.5 pt-0.5"
          >
            {folded}
          </nav>
        </aside>
      </>
    );
  }

  return (
    <>
      {phone}
      <aside
        className="border-border/60 bg-sidebar/30 hidden shrink-0 flex-col border-r py-3 md:flex"
        style={{ width: rail.width, minWidth }}
      >
        <div className="flex items-center justify-between px-4 pb-2">
          <p className={MICRO_LABEL}>{title}</p>
          <button
            type="button"
            onClick={() => setFolded(true)}
            aria-label={`Collapse ${foldLabel}`}
            className="text-muted-foreground hover:text-foreground -mr-1 rounded p-1"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
        </div>
        <nav
          aria-label={label}
          aria-busy={busy || undefined}
          className="min-h-0 flex-1 overflow-y-auto px-2"
        >
          {children}
        </nav>
        {footer && (
          <div className="text-muted-foreground shrink-0 px-4 pt-3 text-[11px] leading-snug">
            {footer}
          </div>
        )}
      </aside>
      {/* The divider between the rail and the content, so a sibling of the
          aside rather than a child. Hidden with the rail below `md`, so a
          phone is not left with a grab strip for a column it cannot see. */}
      <div className="hidden md:flex">
        <ResizeHandle onPointerDown={rail.onResizeStart} />
      </div>
    </>
  );
}

/** Below `md`, the width at which both rails are hidden. */
const PHONE_QUERY = "(max-width: 767px)";

function usePhoneWidth(): boolean {
  const [phone, setPhone] = useState(
    () =>
      typeof window !== "undefined" && window.matchMedia(PHONE_QUERY).matches,
  );
  useEffect(() => {
    const query = window.matchMedia(PHONE_QUERY);
    const onChange = () => setPhone(query.matches);
    onChange();
    query.addEventListener?.("change", onChange);
    return () => query.removeEventListener?.("change", onChange);
  }, []);
  return phone;
}

/**
 * The rail at phone width: a bar above the page with one button that opens the
 * open rail's rows in a drawer from the left.
 *
 * Both widths of the rail are hidden below `md`, which left a phone with no way
 * between Desk's queues, the credentials page's organizations or the analytics
 * levels. The drawer lists the same rows the open rail does, so the two can
 * never disagree about where a section leads. Following a row closes it, as a
 * change of page does, the same as the main navigation's drawer.
 */
function PhoneRail({
  title,
  label,
  busy,
  footer,
  children,
}: {
  title: string;
  label: string;
  busy: boolean;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);

  const closeOnLink = (event: MouseEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).closest("a[href]")) setOpen(false);
  };

  return (
    <div className="border-border/60 shrink-0 border-b px-4 py-2 md:hidden">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="border-border text-foreground hover:bg-muted/60 inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm"
      >
        <PanelLeft className="h-4 w-4" aria-hidden="true" />
        {title}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="left"
          aria-describedby={undefined}
          className="bg-sidebar flex w-72 flex-col p-0 py-3 sm:max-w-none"
          onClick={closeOnLink}
        >
          <SheetTitle className={cn(MICRO_LABEL, "px-4 pb-2")}>
            {title}
          </SheetTitle>
          <nav
            aria-label={label}
            aria-busy={busy || undefined}
            className="min-h-0 flex-1 overflow-y-auto px-2"
          >
            {children}
          </nav>
          {footer && (
            <div className="text-muted-foreground shrink-0 px-4 pt-3 text-[11px] leading-snug">
              {footer}
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

/**
 * Whether the rail is folded, kept in the browser beside its width.
 *
 * It was a `?rail=0` query flag first, and that undid itself: every icon on
 * the folded rail leads somewhere, the link carries no flag, so choosing a
 * surface unfolded the rail on arrival.
 */
function useRailFold(key: string): [boolean, (next: boolean) => void] {
  const [folded, setFolded] = useState<boolean>(() => {
    try {
      return localStorage.getItem(key) === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(key, folded ? "1" : "0");
    } catch {
      // Private mode or blocked storage: the fold still works for the session.
    }
  }, [key, folded]);
  return [folded, setFolded];
}

const rowTone = (selected: boolean) =>
  selected
    ? "bg-muted text-foreground font-medium"
    : "text-muted-foreground hover:bg-muted/60 hover:text-foreground";

/**
 * A count, in the rail's voice: a plain number with no tone. It says how many,
 * not how bad; the colour that would say whether seven is a problem belongs to
 * the things counted. `tracking-normal` so a count inside a tracked band
 * heading still reads as a number.
 */
export function RailCount({ value }: { value: number }) {
  return (
    <span className="text-muted-foreground shrink-0 font-mono text-[11px] tracking-normal tabular-nums">
      {value}
    </span>
  );
}

interface RowContentProps {
  icon: ElementType;
  label: string;
  count?: number;
  /**
   * The row has a count that has not arrived. It holds the count's place with
   * a skeleton, so the row does not reflow when the number lands, and it is
   * not a zero: a number that has not arrived is not a section with no rows.
   */
  countPending?: boolean;
  /** A mark beside the count, such as an attention dot. */
  marker?: ReactNode;
}

/**
 * Icon, name, marker and count, top-aligned so a name that wraps keeps its
 * icon and its count level with its first line. Each sits in a box one text
 * line tall, which is what lines them up without per-item nudges.
 */
function RowContent({
  icon: Icon,
  label,
  count,
  countPending = false,
  marker,
}: RowContentProps) {
  return (
    <>
      <span className="flex h-5 shrink-0 items-center">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      <span className="min-w-0 flex-1 break-words">{label}</span>
      {(marker || countPending || count !== undefined) && (
        <span className="flex h-5 shrink-0 items-center gap-2">
          {marker}
          {countPending ? (
            <Skeleton className="h-3 w-5" aria-hidden />
          ) : (
            count !== undefined && <RailCount value={count} />
          )}
        </span>
      )}
    </>
  );
}

/** A row that goes somewhere: selected when its route is the one in view. */
export function RailLink({
  to,
  end,
  ...content
}: RowContentProps & { to: string; end?: boolean }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex items-start gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
          rowTone(isActive),
        )
      }
    >
      <RowContent {...content} />
    </NavLink>
  );
}

/**
 * A row that chooses something in place: selected when the caller says so.
 *
 * `trailing` is a second control on the row that is not the row's own action,
 * such as the chevron that opens a tree node. It sits outside the row's button
 * so the two are separate targets, and `depth` indents a node under its parent.
 */
export function RailButton({
  selected,
  onSelect,
  depth = 0,
  trailing,
  ...content
}: RowContentProps & {
  selected: boolean;
  onSelect: () => void;
  depth?: number;
  trailing?: ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex items-start rounded-md text-sm transition-colors",
        rowTone(selected),
      )}
    >
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? "true" : undefined}
        className="flex min-w-0 flex-1 items-start gap-2.5 py-2 pr-3 text-left"
        style={{ paddingLeft: 12 + depth * 16 }}
      >
        <RowContent {...content} />
      </button>
      {/* Level with the first line: the row's 8px of padding, then centred
          in its 20px line. */}
      {trailing && (
        <span className="mr-1.5 mt-[7px] flex shrink-0">{trailing}</span>
      )}
    </div>
  );
}

/** A band: its heading, then its rows. */
export function RailBand({
  heading,
  children,
}: {
  heading: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="mt-4 space-y-0.5">
      {heading}
      {children}
    </div>
  );
}

/**
 * A band's heading, in the panel eyebrow's voice.
 *
 * A plain label when the band is a kind of thing (Desk's Work and Manage). A
 * button when the band is itself something the reader can choose, such as an
 * organization whose rows are its children: then it takes the same wash as a
 * row when selected. Baseline-aligned, so the count stays level with the
 * first line of a name that wraps.
 */
export function RailBandHeading({
  label,
  count,
  marker,
  selected = false,
  onSelect,
}: {
  label: string;
  count?: number;
  marker?: ReactNode;
  selected?: boolean;
  onSelect?: () => void;
}) {
  const body = (
    <>
      <span className="min-w-0 flex-1 break-words">{label}</span>
      {marker}
      {count !== undefined && <RailCount value={count} />}
    </>
  );
  if (!onSelect) {
    return (
      <p className={cn(MICRO_LABEL, "flex items-baseline gap-2 px-3 pb-1")}>
        {body}
      </p>
    );
  }
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-current={selected ? "true" : undefined}
      className={cn(
        MICRO_LABEL,
        "flex w-full items-baseline gap-2 rounded-md px-3 py-1 text-left transition-colors",
        selected ? "bg-muted text-foreground" : "hover:text-foreground",
      )}
    >
      {body}
    </button>
  );
}

/**
 * A run of icons on the folded rail. `divided` starts a new band with a
 * hairline, so the fold keeps the open rail's grouping without its words.
 */
export function RailIconGroup({
  label,
  divided = false,
  children,
}: {
  label?: string;
  divided?: boolean;
  children: ReactNode;
}) {
  return (
    <div
      role={label ? "group" : undefined}
      aria-label={label}
      className={cn(
        "flex flex-col items-center gap-0.5",
        divided && "border-border/60 mt-2 border-t pt-2",
      )}
    >
      {children}
    </div>
  );
}

type RailIconProps = {
  icon: ElementType;
  /** The name the fold removed. The tooltip shows it. */
  name: string;
  /** The accessible name, when it says more than `name` and `count`. */
  ariaLabel?: string;
  count?: number;
  /**
   * The count has not arrived: no badge and no number in the name until it
   * has, rather than a zero the rail cannot stand behind.
   */
  countPending?: boolean;
  /** A further line in the tooltip, such as how many need attention. */
  note?: ReactNode;
  /** Something beneath needs a person: a dot on the corner opposite the count. */
  attention?: boolean;
} & ({ to: string; end?: boolean } | { onSelect: () => void; active: boolean });

/**
 * One row on the folded rail: its icon, with the count worn on the corner and
 * the name in a tooltip. Over 99 the badge would outgrow the icon, so it caps;
 * the tooltip and the accessible name carry the real number.
 */
export function RailIcon(props: RailIconProps) {
  const { icon: Icon, name, note, attention = false } = props;
  const count = props.countPending ? undefined : props.count;
  const ariaLabel =
    props.ariaLabel ?? (count !== undefined ? `${name}, ${count}` : name);
  const tip = (
    <>
      <span>{name}</span>
      {count !== undefined && (
        <span className="text-muted-foreground font-mono text-[11px] tabular-nums">
          {count}
        </span>
      )}
      {note}
    </>
  );
  const marks = (
    <>
      <Icon className="h-4 w-4" aria-hidden />
      {count !== undefined && (
        <span
          aria-hidden
          className="bg-muted text-foreground border-background absolute -right-0.5 -top-0.5 min-w-[16px] rounded-full border px-1 text-center font-mono text-[9px] leading-[14px] tabular-nums"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
      {attention && (
        <span
          aria-hidden
          className="bg-destructive absolute -bottom-0.5 -right-0.5 h-1.5 w-1.5 rounded-full"
        />
      )}
    </>
  );
  const shape =
    "relative flex h-9 w-9 shrink-0 items-center justify-center rounded-md transition-colors";

  return (
    <Hint content={tip} side="right" className="flex items-center gap-2">
      {"to" in props ? (
        <NavLink
          to={props.to}
          end={props.end}
          aria-label={ariaLabel}
          // A string, never the `({ isActive }) => ...` form the open rail
          // uses. The tooltip trigger slots its props onto this link and joins
          // class names as strings, which turns a function into its own source
          // text: no `relative`, so every badge piled up at one corner of the
          // rail. `aria-current` is what NavLink sets when the route matches,
          // so the active wash keys off that instead.
          className={cn(
            shape,
            "text-muted-foreground hover:bg-muted/60 hover:text-foreground aria-[current=page]:bg-muted aria-[current=page]:text-foreground",
          )}
        >
          {marks}
        </NavLink>
      ) : (
        <button
          type="button"
          onClick={props.onSelect}
          aria-label={ariaLabel}
          aria-current={props.active ? "true" : undefined}
          className={cn(
            shape,
            props.active
              ? "bg-muted text-foreground"
              : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
          )}
        >
          {marks}
        </button>
      )}
    </Hint>
  );
}
